import React from "react";
import { Link } from "react-router-dom";
import { Phone, ArrowRight, AlertTriangle } from "lucide-react";
import { COMPANY } from "@/data/company";
import { trackPhoneClick } from "@/lib/track";

/**
 * CTABanner — two variants:
 *   variant="emergency"  → red-tinted, phone-first, for breakdown-flavoured pages
 *   variant="standard"   → blue accent, form-first, for planned work / general pages
 *
 * Props:
 *  - variant: "emergency" | "standard"           (default "standard")
 *  - title, body                                 override defaults
 *  - phoneLocation: string                       GA4 label for phone tap
 *  - formHref: string                            defaults to "/contact"
 */
const CTABanner = ({
  variant = "standard",
  title,
  body,
  phoneLocation = "cta_banner",
  formHref = "/contact",
}) => {
  const isEmergency = variant === "emergency";

  const defaultTitle = isEmergency
    ? "Breakdown right now? Speak to an engineer in under 60 seconds."
    : "Got a project, a survey or a compliance deadline?";
  const defaultBody = isEmergency
    ? "24/7 rapid response for line-down mechanical, electrical and controls emergencies across the North West."
    : "Talk to a working engineer — not a call centre. Every enquiry gets a same-day human reply.";

  const accent = isEmergency
    ? {
        // warm red accent for urgency
        border: "1px solid rgba(255, 107, 90, 0.35)",
        background:
          "linear-gradient(135deg, rgba(58, 14, 14, 0.6) 0%, rgba(6, 11, 24, 0.85) 55%, rgba(28, 8, 6, 0.6) 100%)",
        shadow: "0 30px 80px -20px rgba(255, 90, 60, 0.18)",
        iconColor: "#ff8a75",
        chipBg: "rgba(255, 107, 90, 0.14)",
        chipBorder: "1px solid rgba(255, 107, 90, 0.4)",
        chipText: "#ffb3a4",
      }
    : {
        border: "1px solid var(--lt-border-strong)",
        background:
          "linear-gradient(135deg, rgba(20, 34, 68, 0.55) 0%, rgba(6, 11, 24, 0.9) 55%, rgba(15, 26, 51, 0.6) 100%)",
        shadow: "0 30px 80px -30px rgba(58, 108, 255, 0.28)",
        iconColor: "var(--lt-blue-bright)",
        chipBg: "rgba(58, 108, 255, 0.14)",
        chipBorder: "1px solid rgba(127, 166, 255, 0.35)",
        chipText: "#c9d8ff",
      };

  return (
    <section
      className="max-w-7xl mx-auto px-5 lg:px-10 my-20 lg:my-28"
      data-testid={`cta-banner-${variant}`}
    >
      <div
        className="rounded-2xl p-8 lg:p-12 flex flex-col lg:flex-row lg:items-center gap-8 lg:gap-12 relative overflow-hidden"
        style={{
          border: accent.border,
          background: accent.background,
          boxShadow: accent.shadow,
        }}
      >
        <div className="flex-1 relative">
          <span
            className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.24em]"
            style={{
              background: accent.chipBg,
              border: accent.chipBorder,
              color: accent.chipText,
            }}
          >
            {isEmergency ? (
              <>
                <AlertTriangle size={12} /> 24/7 Emergency
              </>
            ) : (
              "Talk to us"
            )}
          </span>
          <h3
            className="font-display mt-4 text-white"
            style={{ fontSize: "clamp(24px, 3vw, 36px)", lineHeight: 1.05 }}
          >
            {title || defaultTitle}
          </h3>
          <p className="mt-3 text-sm lg:text-[15px] leading-relaxed text-[color:var(--lt-text-dim)] max-w-xl">
            {body || defaultBody}
          </p>
        </div>

        <div className="flex flex-col sm:flex-row lg:flex-col gap-3 flex-shrink-0 relative">
          <a
            href={COMPANY.phone.tel}
            onClick={() => trackPhoneClick(phoneLocation)}
            className={`inline-flex items-center justify-center gap-2 rounded-md px-5 py-3.5 text-sm font-semibold transition-transform hover:scale-[1.02] ${
              isEmergency ? "" : "btn-primary"
            }`}
            style={
              isEmergency
                ? {
                    background: "#e34d3a",
                    color: "white",
                    boxShadow: "0 10px 30px -10px rgba(227, 77, 58, 0.65)",
                  }
                : undefined
            }
            data-testid={`cta-banner-phone-${variant}`}
          >
            <Phone size={16} /> Call 24/7 · {COMPANY.phone.display}
          </a>
          <Link
            to={formHref}
            className={`inline-flex items-center justify-center gap-2 rounded-md px-5 py-3.5 text-sm font-semibold ${
              isEmergency ? "btn-ghost" : "btn-ghost"
            }`}
            data-testid={`cta-banner-form-${variant}`}
          >
            Send an enquiry <ArrowRight size={15} />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default CTABanner;
